import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreatePaymentReceipt = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    invoice_id: '',
    payment_amount: '',
    payment_method: '',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchUnpaidInvoices();
  }, []);

  const fetchUnpaidInvoices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/`);
      const unpaid = response.data.filter(inv => !inv.paid);
      setInvoices(unpaid);
    } catch (error) {
      toast.error('Failed to load invoices');
      console.error(error);
    }
  };

  const selectInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invoices/${invoiceId}`);
      const invoice = response.data;
      
      setSelectedInvoice(invoice);
      setFormData({
        ...formData,
        invoice_id: invoiceId,
        payment_amount: invoice.total_amount.toString()
      });
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const receiptData = {
        ...formData,
        payment_amount: parseFloat(formData.payment_amount)
      };

      const response = await axios.post(`${BACKEND_URL}/api/payment-receipts/`, receiptData);

      if (response.data.success) {
        toast.success(response.data.message);
        navigate('/admin/billing');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create payment receipt'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create Payment Receipt</h1>
              <p className="text-gray-600 mt-1">Record payment received from customer</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <div className="max-w-3xl mx-auto space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Select Invoice</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Unpaid Invoice *</Label>
                    <Select 
                      value={formData.invoice_id}
                      onValueChange={selectInvoice}
                    >
                      <SelectTrigger data-testid="invoice-select">
                        <SelectValue placeholder="Select invoice..." />
                      </SelectTrigger>
                      <SelectContent>
                        {invoices.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500">No unpaid invoices found</div>
                        ) : (
                          invoices.map(inv => (
                            <SelectItem key={inv.id} value={inv.id}>
                              {inv.document_number} - {inv.customer_name} (${inv.total_amount.toFixed(2)})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedInvoice && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2">Invoice Details</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-600">Customer:</span> {selectedInvoice.customer_name}</p>
                        <p><span className="text-gray-600">Amount Due:</span> <span className="font-bold text-lg">${selectedInvoice.total_amount.toFixed(2)}</span></p>
                        <p><span className="text-gray-600">Invoice Date:</span> {new Date(selectedInvoice.invoice_date || selectedInvoice.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Amount ($) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.payment_amount}
                      onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                      required
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Payment Date *</Label>
                    <Input
                      type="date"
                      value={formData.payment_date}
                      onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                      required
                      data-testid="payment-date-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Method *</Label>
                    <Select 
                      value={formData.payment_method}
                      onValueChange={(value) => setFormData({...formData, payment_method: value})}
                    >
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue placeholder="Select payment method..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="PayNow">PayNow</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                        <SelectItem value="Credit Card">Credit Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reference Number</Label>
                    <Input
                      value={formData.reference_number}
                      onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                      placeholder="Transaction ref, cheque no., etc."
                      data-testid="reference-number-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                    placeholder="Additional payment notes..."
                    data-testid="payment-notes-textarea"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/billing')}
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={loading || !formData.invoice_id || !formData.payment_method}
                data-testid="create-receipt-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save size={20} />
                    Create Payment Receipt
                  </span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
